import { css } from '@emotion/react';
import ClearIcon from '@mui/icons-material/Clear';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { DragEvent, ReactNode, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';

import { convertFileSize } from '../../../pages/KPI/utils';
import { UnstyledFieldset } from '../../../pages/CompanyProfiles/Scenarios/components/commonStyledComponents';
import { colors } from '../../../theme/colors';

const DefaultMaxFileSize = 30 * 1024 * 1024;

const FileInputBox = css`
  width: 100%;
  min-height: 3rem;
  border-radius: 4px;
  display: grid;
  align-items: center;
  padding-inline: 1rem;
  grid-template-columns: 1fr;
  transition:
    background-color 150ms,
    border-color 150ms,
    box-shadow 150ms;
`;

const EmptyFileInputBox = css`
  ${FileInputBox};
  border: thin dashed ${colors.neutral[30]};
  background: #282e9a0a;
  &:disabled {
    pointer-events: none;
  }

  /* Hover: same wash a shade stronger (4% → 6%), and the browse link underlines.
     The underline is inherited from here, not the link's own :hover — hovering
     anywhere in the zone is what arms the click in clickToOpen mode. */
  &:not([aria-disabled='true']):hover {
    background: #282e9a17;
  }
  &:not([aria-disabled='true']):hover button {
    text-decoration: underline;
  }

  /* Inset ring instead of a thicker border so the highlight never shifts layout. */
  &[data-drag-active] {
    border-color: transparent;
    box-shadow: inset 0 0 0 2px var(--mui-palette-primary-main);
    background: color-mix(in srgb, var(--mui-palette-primary-main) 12%, transparent);
  }
`;

const FileInputButton = css`
  all: unset;
  position: relative;
`;

const InputDescContainer = css`
  display: flex;
  justify-content: center;
  align-items: center;

  input[type=file], /* FF, IE7+, chrome (except button) */
  input[type=file]::-webkit-file-upload-button {
    /* chromes and blink button */
    cursor: pointer;
  }
  & input {
    all: unset;
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: 100%;
  }
`;

export interface FileDropFieldProps {
  value: File | File[] | null;
  onChange: (value: File | File[] | null) => void;
  multiple?: boolean;
  disabled?: boolean;
  /** MIME types for drop filtering, and the default native `accept` filter. */
  acceptTypes?: string[];
  /**
   * Overrides the native `<input accept>` filter (e.g. an extension list like
   * `.pdf,.zip`). Decoupled from `acceptTypes` so the picker hint can differ from
   * the MIME-based drop filter. Defaults to `acceptTypes` joined.
   */
  accept?: string;
  /** Extensions shown in the default helper text (uppercased). */
  acceptExtensions?: string[];
  maxFileSizeMb?: number;
  /** Copy overrides — default to the form-field look so existing usages are unchanged. */
  primaryText?: ReactNode;
  browseLabel?: ReactNode;
  helperText?: ReactNode;
  /** When true, clicking anywhere in the zone opens the file picker (not just the browse link). */
  clickToOpen?: boolean;
  zoneTestId?: string;
  inputTestId?: string;
}

/**
 * Presentational file dropzone: drag-and-drop or browse, emitting `File`/`File[]`
 * via `onChange`. Framework-agnostic — `FieldFile2` adapts the form-field props
 * to it, and the Document Hub upload zone drives it directly.
 */
export function FileDropField({
  value,
  onChange,
  multiple = false,
  disabled = false,
  acceptTypes,
  accept,
  acceptExtensions,
  maxFileSizeMb = DefaultMaxFileSize,
  primaryText,
  browseLabel = 'Browse your files',
  helperText,
  clickToOpen = false,
  zoneTestId,
  inputTestId,
}: FileDropFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Depth counter: dragenter/dragleave also fire when moving across child nodes,
  // so a plain boolean would flicker mid-drag.
  const dragDepthRef = useRef(0);
  const [isDragActive, setIsDragActive] = useState(false);

  const resetDragState = useCallback(() => {
    dragDepthRef.current = 0;
    setIsDragActive(false);
  }, []);

  /*
   * Fallback reset.
   *
   * The zone's own dragleave is not a guarantee: a drag from the OS fires no `dragend`
   * in the page, and Escape-cancel and drag-abort behaviour differ across browsers, so a
   * missed dragleave would latch `isDragActive` on forever. That is not merely cosmetic
   * — the Browse button is hidden while active, and `FieldFile2` does not pass
   * `clickToOpen`, so a stuck zone leaves that field with no way to pick a file at all
   * until it remounts.
   *
   * Capture phase, because a drop handled elsewhere on the page may stop propagation.
   */
  useEffect(() => {
    window.addEventListener('dragend', resetDragState, true);
    window.addEventListener('drop', resetDragState, true);

    return () => {
      window.removeEventListener('dragend', resetDragState, true);
      window.removeEventListener('drop', resetDragState, true);
    };
  }, [resetDragState]);

  const _onChange = (e: SyntheticEvent) => {
    e.preventDefault();
    const input = fileInputRef.current;
    if (!input?.files) {
      return;
    }
    if (multiple) {
      onChange(Array.from(input.files));
    } else {
      onChange(input.files[0]);
    }
    // Reset the native input so re-selecting the SAME file fires `change` again.
    // Without this, a browser suppresses the event when the value is unchanged —
    // e.g. retrying a file after its upload errored would silently do nothing.
    input.value = '';
  };

  const handleDrag = (e: SyntheticEvent) => {
    e.preventDefault();
  };

  /**
   * True only for a drag carrying files.
   *
   * Without this any drag activates the zone — selected page text, an image from another
   * tab, an ag-grid column header — promising "Drop files to upload" for a drop that can
   * deliver nothing. In the `multiple` case the drop then still called `onChange` with a
   * fresh array, marking the form dirty for a drag that contained no files at all.
   */
  const carriesFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    if (disabled || !carriesFiles(e)) {
      return;
    }
    dragDepthRef.current += 1;
    setIsDragActive(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    resetDragState();
    // Mirror the old useFileDragDrop guard: a disabled/read-only zone must not
    // accept drops. The <fieldset disabled> only inerts form controls, not this
    // div's onDrop, so guard here explicitly.
    if (disabled) {
      return;
    }
    if (multiple) {
      const validFiles = Array.from(e.dataTransfer.files).filter(
        (file) => !acceptTypes || acceptTypes.includes(file.type)
      );
      // A drop that contributed nothing must not call onChange. It would hand back a
      // fresh array with identical contents, which is a new reference — enough to mark
      // the form dirty for a drag that carried no files, or none of an accepted type.
      if (validFiles.length === 0) {
        return;
      }
      onChange([...((value as File[]) ?? []), ...validFiles]);
    } else {
      const selected = e.dataTransfer.files[0];
      if (selected && (!acceptTypes || acceptTypes.includes(selected.type))) {
        onChange(selected);
      }
    }
  };

  const handleRemove = (file: File) => {
    if (multiple) {
      onChange((value as File[]).filter((f) => f !== file));
    } else {
      onChange(null);
    }
  };

  const openPicker = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const valueAsArray: File[] = multiple ? ((value as File[]) ?? []) : value ? [value as File] : [];

  return (
    <UnstyledFieldset disabled={disabled}>
      <div
        css={EmptyFileInputBox}
        data-testid={zoneTestId}
        data-drag-active={isDragActive || undefined}
        aria-disabled={disabled}
        onDragOver={handleDrag}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        {...(clickToOpen ? { role: 'button', onClick: openPicker } : {})}
      >
        <Stack gap='0.5rem' mb='1.5rem' alignItems='center' justifyContent='center' pt='1rem'>
          <div css={InputDescContainer}>
            {/*
              Colour via `sx`, not `htmlColor`. MUI forwards `htmlColor` onto the <svg>
              as a presentation *attribute*, and `var()` is not substituted in
              presentation attributes on Chromium or WebKit — so the drag-active colour
              silently did nothing outside Firefox.
            */}
            <CloudUploadOutlinedIcon
              sx={{ color: isDragActive ? 'primary.main' : colors.neutral[50], mr: '0.25rem' }}
              fontSize='small'
            />
            {isDragActive ? (
              <Typography variant='body2' sx={{ color: 'primary.main', fontWeight: 500 }}>
                Drop files to upload
              </Typography>
            ) : (
              <Typography variant='body2' color='text.secondary'>
                {primaryText ?? <>Drag and drop or&nbsp;</>}
              </Typography>
            )}
            {/*
              Hidden (not unmounted) while dragging: the native input must stay in the DOM.

              `visibility` + `pointer-events`, never `display: none`. Removing the element
              under the cursor forces the browser to re-target on the next drag tick, and
              where dragleave on the old target precedes dragenter on the new one the depth
              counter dips through zero — reintroducing exactly the flicker that counter
              exists to prevent. Kept in the layout but untargetable, the drag never
              re-targets at all.
            */}
            <button
              css={FileInputButton}
              type='button'
              style={isDragActive ? { visibility: 'hidden', pointerEvents: 'none' } : undefined}
            >
              <Typography variant='body2' color='secondary'>
                {browseLabel}
              </Typography>
              <input
                type='file'
                ref={fileInputRef}
                data-testid={inputTestId}
                onChange={_onChange}
                multiple={multiple}
                accept={accept ?? acceptTypes?.join(', ')}
                // In click-to-open mode the zone also opens the picker; stop the
                // input's own click from bubbling so it doesn't reopen twice.
                onClick={clickToOpen ? (e) => e.stopPropagation() : undefined}
              />
            </button>
          </div>
          <Typography variant='body2' sx={{ color: '#10253E80' }}>
            {helperText ?? (
              // JSX children (not a template literal) so an undefined
              // acceptExtensions renders as nothing, not the string "undefined".
              <>
                {acceptExtensions?.map((ext) => ext.toUpperCase()).join(', ')} formats, up to{' '}
                {(maxFileSizeMb / 1024 / 1024).toFixed(1)}MB
              </>
            )}
          </Typography>
          {valueAsArray.length > 0 &&
            valueAsArray.map((file) => (
              <Box
                display='flex'
                gap='0.5rem'
                alignItems={'center'}
                justifyContent={'space-between'}
                width={'100%'}
                key={file.name}
              >
                <Stack direction={'row'} gap='.5rem' justifyContent={'center'} alignItems={'center'}>
                  <InsertDriveFileOutlinedIcon sx={{ color: 'text.secondary' }} fontSize='medium' />
                  <Typography variant='body2' color='text.primary' noWrap>
                    {`${file.name} ${convertFileSize(file.size)}`}
                  </Typography>
                </Stack>
                {!disabled && (
                  <IconButton onClick={() => handleRemove(file)} title='remove'>
                    <ClearIcon htmlColor={colors.neutral[50]} fontSize='small' />
                  </IconButton>
                )}
              </Box>
            ))}
        </Stack>
      </div>
    </UnstyledFieldset>
  );
}
